// src/features/system/orchestrator/components/CreateWorkflowDialog.tsx
import React, { useState } from 'react';
import { useEscapeLayer } from '@/shared/hooks/useEscapeLayer';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import AppDialog from '@/shared/components/ui/AppDialog';
import { WorkflowCatalogEntry } from '../api/workflowsApi';
import type { Handler } from '../api/handlersApi';
import { fetchHandlersV2Api, type HandlerSpecRow } from '../api/handlersV2Api';
import { fetchWorkflowsV2Api, publishWorkflowV2Api, type StartWorkflowResponse as StartWorkflowResponseV2 } from '../api/workflowsV2Api';
import { fetchWorkersV2Api } from '../api/workersApi';
import { useAiText } from '@/features/system/ai/hooks/useAiText';
import { WORKFLOW_DAG_AI_SYSTEM_PROMPT, buildWorkflowDagAiUserPrompt } from '../ai/workflowDagAiPrompt';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: (workflow: WorkflowCatalogEntry) => void;
  // Legacy callers may still pass this; the dialog currently uses v2 sources internally.
  handlersSource?: 'v1' | 'v2';
}

type PublishWorkflowInput = {
  workflow_key: string;
  // v2 publish supports server-side auto-bump when version=0
  version: number;
  definition: unknown;
  is_active: boolean;
};

type FormState = {
  workflow_key: string;
  is_active: boolean;
  definitionJson: string;
};

const DEFAULT_FORM: FormState = {
  workflow_key  : '',
  is_active     : true,
  definitionJson: '{\n  "dag": {}\n}',
};

function mapV2HandlerRowToLegacyHandler(row: HandlerSpecRow, workerIdByKey: Map<string, number>): Handler | null {
  const handler_key = String(row.handler_key ?? '').trim();
  if (!handler_key) return null;

  const worker_key = String(row.worker_key ?? '').trim();
  const worker_id = worker_key ? workerIdByKey.get(worker_key) : undefined;
  if (!worker_id) return null;

  const tags = Array.isArray(row.tags) ? row.tags : [];

  return {
    handler_key,
    worker_id,
    description: row.description != null ? String(row.description) : null,
    timeout_sec: Number.isFinite(Number(row.timeout_sec)) ? Number(row.timeout_sec) : 300,
    max_retries: Number.isFinite(Number(row.max_retries)) ? Number(row.max_retries) : 3,
    backoff_ms: Number.isFinite(Number(row.backoff_ms)) ? Number(row.backoff_ms) : 1000,
    priority: Number.isFinite(Number(row.priority)) ? Number(row.priority) : 0,
    category: null,
    tags,
    config: undefined,
    ability_key: null,
    is_active: row.is_active == null ? true : Boolean(row.is_active),
    created_at: new Date(0).toISOString(),
    updated_at: row.updated_at ? String(row.updated_at) : new Date(0).toISOString(),
  };
}

export default function CreateWorkflowDialog(props: Props) {
  const { open, onOpenChange, onCreated } = props;
  const qc = useQueryClient();
  const aiText = useAiText();
  const [form, setForm] = useState<FormState>(DEFAULT_FORM);
  const [error, setError] = useState<string | null>(null);

  const [existsConfirmOpen, setExistsConfirmOpen] = useState(false);
  const [existsConfirmBusy, setExistsConfirmBusy] = useState(false);
  const pendingPublishRef = React.useRef<null | { workflow_key: string; version: number; definition: unknown; is_active: boolean }>(null);
  const pendingExistsMaxVersionRef = React.useRef<number>(0);

  const [aiOpen, setAiOpen] = useState(false);
  const [aiStep, setAiStep] = useState<1 | 2 | 3 | 4>(1);
  const [aiGoal, setAiGoal] = useState('');
  const [aiInputs, setAiInputs] = useState('');
  const [aiOutputs, setAiOutputs] = useState('');
  const [aiNotes, setAiNotes] = useState('');
  const [aiBusy, setAiBusy] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  useEscapeLayer(() => { setAiOpen(false); setAiError(null); }, aiOpen && !aiBusy);

  const { data: handlers = [], isLoading: handlersLoading } = useQuery<Handler[], Error>({
    queryKey: ['admin', 'orchestrator', 'handlers', 'workflow-create-ai', 'v2'],
    queryFn: async () => {
      const workersRes = await fetchWorkersV2Api({ limit: 500 });
      const workerIdByKey = new Map<string, number>();
      for (const w of workersRes.items ?? []) {
        const wk = String(w.worker_key ?? '').trim();
        if (!wk) continue;
        if (typeof w.worker_id === 'number' && Number.isFinite(w.worker_id)) workerIdByKey.set(wk, w.worker_id);
      }

      const res = await fetchHandlersV2Api({ limit: 500 });
      const mapped: Handler[] = [];
      for (const row of res.items ?? []) {
        const h = mapV2HandlerRowToLegacyHandler(row, workerIdByKey);
        if (h) mapped.push(h);
      }
      return mapped;
    },
    enabled: open && aiOpen,
  });

  const v2Mutation = useMutation<StartWorkflowResponseV2, Error, PublishWorkflowInput>({
    mutationFn: (payload) => publishWorkflowV2Api({
      workflow_key: payload.workflow_key,
      // v2 publish supports server-side auto-bump; default to it.
      version: 0,
      definition: payload.definition,
      is_active: payload.is_active,
    }),
    onSuccess: async (_started, vars) => {
      qc.invalidateQueries({ queryKey: ['admin', 'orchestrator', 'workflows', 'v2'] });
      window.setTimeout(() => {
        qc.invalidateQueries({ queryKey: ['admin', 'orchestrator', 'workflows', 'v2'] });
      }, 1500);

      // Try to resolve the newly-created workflow row so callers can open it in the builder.
      // (Publish is async; the row may appear slightly later.)
      if (onCreated) {
        try {
          const delay = (ms: number) => new Promise((r) => window.setTimeout(r, ms));
          let maxVersion = 0;
          for (let attempt = 0; attempt < 6; attempt++) {
            const res = await fetchWorkflowsV2Api({ limit: 500 });
            for (const row of res.items ?? []) {
              if (row?.workflow_key !== vars.workflow_key) continue;
              const v = Number(row.version);
              if (Number.isFinite(v) && v > maxVersion) maxVersion = v;
            }
            if (maxVersion > 0) break;
            await delay(350);
          }

          if (maxVersion > 0) {
            const now = new Date().toISOString();
            onCreated({
              workflow_key: vars.workflow_key,
              version: maxVersion,
              definition: vars.definition,
              is_active: vars.is_active,
              created_at: now,
              updated_at: now,
            });
          }
        } catch {
          // Non-fatal: the catalog refresh will still allow selecting the workflow.
        }
      }

      // v2 publish is async + auto-bumps server-side, so we don't know the final version here.
      // Rely on the catalog refresh instead of emitting a potentially wrong optimistic row.

      setForm(DEFAULT_FORM);
      setError(null);
      onOpenChange(false);
    },
    onError: (err) => setError(err?.message || 'Failed to publish workflow'),
  });

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const parseDefinition = (): unknown => {
    const raw = form.definitionJson.trim();
    if (!raw) throw new Error('Definition must be valid JSON.');
    try {
      return JSON.parse(raw);
    } catch {
      throw new Error('Definition must be valid JSON.');
    }
  };

  const canSave =
    form.workflow_key.trim().length > 0 &&
    form.definitionJson.trim().length > 0 &&
    !v2Mutation.isPending;

  const parseAiJson = (text: string): unknown => {
    const raw = text.trim();
    if (!raw) throw new Error('AI returned empty content.');
    try {
      return JSON.parse(raw);
    } catch {
      throw new Error('AI response was not valid JSON.');
    }
  };

  const handleCreateWithAi = async () => {
    setAiError(null);
    const workflowKey = form.workflow_key.trim();
    const goal = aiGoal.trim();
    const inputs = aiInputs.trim();
    const outputs = aiOutputs.trim();
    const notes = aiNotes.trim();

    const specParts: string[] = [];
    if (workflowKey) specParts.push(`Workflow key: ${workflowKey}`);
    if (goal) specParts.push(`Goal:\n${goal}`);
    if (inputs) specParts.push(`Inputs:\n${inputs}`);
    if (outputs) specParts.push(`Outputs:\n${outputs}`);
    if (notes) specParts.push(`Notes/constraints:\n${notes}`);

    const spec = specParts.join('\n\n').trim();
    if (!spec) {
      setAiError('Fill at least one wizard field before creating.');
      return;
    }

    setAiBusy(true);
    try {
      const systemPrompt = WORKFLOW_DAG_AI_SYSTEM_PROMPT;
      const userPrompt = buildWorkflowDagAiUserPrompt(spec, {
        handlers_json: handlers,
      });

      const res = await aiText.generate({
        system_prompt: systemPrompt,
        user_prompt: userPrompt,
        temperature: 0.2,
        max_output_tokens: 4096,
      });
      if (!res) return; // 202 async dispatch — result via SSE

      const json = parseAiJson(res.content);
      const obj = (json && typeof json === 'object' && !Array.isArray(json)) ? (json as Record<string, any>) : null;
      if (!obj) throw new Error('AI response must be a JSON object.');

      const wk = typeof obj.workflow_key === 'string' ? obj.workflow_key.trim() : '';
      const dag = obj.dag;
      if (!wk) throw new Error('AI response missing workflow_key.');
      if (!dag || typeof dag !== 'object' || Array.isArray(dag)) throw new Error('AI response missing dag object.');

      // Basic safety: ensure all chosen services exist in registry.
      const available = new Set(handlers.map((h) => h.handler_key));
      const availableBases = new Set(handlers.map((h) => String(h.handler_key ?? '').trim())
        .filter(Boolean)
        .map((hk) => hk.replace(/(?:\.v\d+|:v?\d+)$/i, '')));
      const steps = (dag as any).steps;
      if (!steps || typeof steps !== 'object' || Array.isArray(steps)) {
        throw new Error('AI dag.steps must be an object keyed by step id.');
      }
      for (const stepId of Object.keys(steps)) {
        const s = (steps as any)[stepId];
        const service = (s && typeof s === 'object') ? String((s as any).service ?? '') : '';
        if (!service.trim()) throw new Error(`AI step ${stepId} is missing service.`);
        const svc = service.trim();
        if (available.has(svc)) continue;
        const m = svc.match(/^(.*):latest$/i);
        if (m) {
          const base = String(m[1] ?? '').trim();
          if (base && availableBases.has(base)) continue;
        }
        throw new Error(`AI step ${stepId} uses unknown handler_key: ${service}`);
      }

      setField('workflow_key', wk);
      setField('definitionJson', JSON.stringify({ dag }, null, 2));
      setAiOpen(false);
      setAiStep(1);
      setAiGoal('');
      setAiInputs('');
      setAiOutputs('');
      setAiNotes('');
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setAiError(msg || 'AI generation failed.');
    } finally {
      setAiBusy(false);
    }
  };

  const handleConfirm = async () => {
    setError(null);
    if (!canSave) return;

    let definition: unknown;
    try {
      definition = parseDefinition();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      return;
    }

    const workflow_key = form.workflow_key.trim();

    try {
      const res = await fetchWorkflowsV2Api({ limit: 500 });
      const list = Array.isArray(res.items) ? res.items : [];
      let max = 0;
      for (const w of list) {
        if (w?.workflow_key !== workflow_key) continue;
        if (typeof w.version === 'number' && Number.isFinite(w.version) && w.version > max) max = w.version;
      }

      const payload: PublishWorkflowInput = { workflow_key, version: 0, definition, is_active: form.is_active };

      if (max > 0) {
        pendingPublishRef.current = payload;
        pendingExistsMaxVersionRef.current = max;
        setExistsConfirmOpen(true);
        return;
      }

      v2Mutation.mutate(payload);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg || 'Failed to check existing workflows');
    }
  };

  const handleClose = (next: boolean) => {
    if (!next) {
      setForm(DEFAULT_FORM);
      setError(null);
      setAiOpen(false);
      setAiStep(1);
      setAiGoal('');
      setAiInputs('');
      setAiOutputs('');
      setAiNotes('');
      setAiBusy(false);
      setAiError(null);
    }
    onOpenChange(next);
  };

  return (
    <>
      <AppDialog
        open={open}
        onOpenChange={handleClose}
        title="Publish workflow"
        description="Store a versioned workflow DAG in the workflow catalog."
        size="lg"
        onConfirm={handleConfirm}
        confirmLabel={v2Mutation.isPending ? 'Publishing…' : 'Publish'}
        confirmDisabled={!canSave}
        isBusy={v2Mutation.isPending}
      >
        <div className="space-y-4">
          {error && <p className="text-sm text-rose-400">{error}</p>}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <LabeledInput
              label="Workflow Key *"
              value={form.workflow_key}
              placeholder="audio_processing_v1"
              onChange={(v) => setField('workflow_key', v)}
            />
          </div>

        <div className="flex items-center justify-between gap-3 rounded pac-glass px-3 py-2">
          <div>
            <div className="text-xs uppercase tracking-wide text-neutral-400">AI</div>
            <div className="text-[11px] text-neutral-500">Generate steps/edges/IO using the handler registry.</div>
          </div>
          <button
            type="button"
            onClick={() => {
              setAiError(null);
              setAiStep(1);
              setAiOpen(true);
            }}
            className="rounded border border-neutral-700 px-3 py-1 text-xs text-neutral-200 hover:border-neutral-500 hover:text-neutral-50"
          >
            Create with AI
          </button>
        </div>

        <label className="flex items-center gap-2 text-sm text-neutral-200">
          <input
            type="checkbox"
            className="accent-emerald-500"
            checked={form.is_active}
            onChange={(e) => setField('is_active', e.target.checked)}
          />
          Active immediately
        </label>

        <LabeledTextarea
          label="Definition (JSON) *"
          value={form.definitionJson}
          placeholder={'{\n  "dag": {}\n}'}
          description="Paste the workflow DAG JSON payload."
          onChange={(v) => setField('definitionJson', v)}
          rows={12}
        />
        </div>

        {aiOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/30 backdrop-blur-sm px-4"
          role="dialog"
          aria-modal="true"
          aria-label="Create workflow with AI"
        >
          <div className="w-full max-w-3xl rounded-lg border border-neutral-800 bg-black p-4 shadow-xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-sm font-medium text-neutral-100">Create workflow with AI</div>
                <div className="text-xs text-neutral-400">
                  Wizard step {aiStep} of 4. Fields are concatenated and sent to the AI.
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  if (!aiBusy) {
                    setAiOpen(false);
                    setAiError(null);
                  }
                }}
                className="rounded border border-neutral-700 bg-black px-2 py-1 text-xs text-neutral-200 hover:bg-rose-600 hover:border-rose-600 hover:text-white transition-colors disabled:opacity-60"
                disabled={aiBusy}
                title={aiBusy ? 'Generating…' : 'Close'}
              >
                Close
              </button>
            </div>

            {aiError && <p className="mt-3 text-sm text-amber-300">{aiError}</p>}

            <div className="mt-3">
              {aiStep === 1 && (
                <>
                  <label className="text-xs uppercase tracking-wide text-neutral-400">WORKFLOW KEY</label>
                  <input
                    value={form.workflow_key}
                    placeholder="youtube_audio_to_gcs_v1"
                    className="mt-1 w-full bg-black border border-neutral-700 rounded px-2 py-2 text-sm outline-none focus:ring-1 focus:ring-indigo-500 disabled:opacity-60"
                    disabled
                  />
                  <label className="mt-3 block text-xs uppercase tracking-wide text-neutral-400">Goal</label>
                  <textarea
                    autoFocus
                    value={aiGoal}
                    onChange={(e) => setAiGoal(e.target.value)}
                    placeholder="Describe what the workflow should accomplish."
                    className="mt-1 w-full bg-black border border-neutral-700 rounded px-2 py-2 text-sm outline-none focus:ring-1 focus:ring-indigo-500 min-h-[140px]"
                  />
                </>
              )}

              {aiStep === 2 && (
                <>
                  <label className="text-xs uppercase tracking-wide text-neutral-400">Inputs</label>
                  <textarea
                    autoFocus
                    value={aiInputs}
                    onChange={(e) => setAiInputs(e.target.value)}
                    placeholder={
                      'Example:\n'
                      + '- youtube_url: string\n'
                      + '- bucket: string\n'
                      + '- tenant_id: string\n'
                    }
                    className="mt-1 w-full bg-black border border-neutral-700 rounded px-2 py-2 text-sm outline-none focus:ring-1 focus:ring-indigo-500 min-h-[220px]"
                  />
                </>
              )}

              {aiStep === 3 && (
                <>
                  <label className="text-xs uppercase tracking-wide text-neutral-400">Outputs</label>
                  <textarea
                    autoFocus
                    value={aiOutputs}
                    onChange={(e) => setAiOutputs(e.target.value)}
                    placeholder={
                      'Example:\n'
                      + '- audio_url: string\n'
                      + '- duration_seconds: integer\n'
                    }
                    className="mt-1 w-full bg-black border border-neutral-700 rounded px-2 py-2 text-sm outline-none focus:ring-1 focus:ring-indigo-500 min-h-[220px]"
                  />
                </>
              )}

              {aiStep === 4 && (
                <>
                  <label className="text-xs uppercase tracking-wide text-neutral-400">Notes / constraints</label>
                  <textarea
                    autoFocus
                    value={aiNotes}
                    onChange={(e) => setAiNotes(e.target.value)}
                    placeholder={
                      'Examples:\n'
                      + '- Prefer cloud handlers when possible.\n'
                      + '- Must upload artifacts to GCS.\n'
                      + '- Timeout 10 minutes.\n'
                    }
                    className="mt-1 w-full bg-black border border-neutral-700 rounded px-2 py-2 text-sm outline-none focus:ring-1 focus:ring-indigo-500 min-h-[220px]"
                  />
                </>
              )}

              <div className="mt-2 text-[11px] text-neutral-500">
                {handlersLoading ? 'Loading handler registry…' : `Handler registry entries: ${handlers.length}`}
              </div>
            </div>

            <div className="mt-4 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  if (!aiBusy) {
                    setAiOpen(false);
                    setAiError(null);
                  }
                }}
                className="rounded border border-neutral-800 px-3 py-1 text-xs text-neutral-200 hover:border-neutral-600 disabled:opacity-60"
                disabled={aiBusy}
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={() => setAiStep((s) => (s > 1 ? ((s - 1) as 1 | 2 | 3 | 4) : s))}
                className="rounded border border-neutral-700 bg-black px-3 py-1 text-xs text-neutral-200 hover:border-neutral-600 hover:border-neutral-600 transition-colors disabled:opacity-60"
                disabled={aiBusy || aiStep === 1}
              >
                Back
              </button>

              {aiStep < 4 ? (
                <button
                  type="button"
                  onClick={() => setAiStep((s) => (s < 4 ? ((s + 1) as 1 | 2 | 3 | 4) : s))}
                  className="rounded border border-neutral-700 bg-black px-3 py-1 text-xs text-neutral-200 hover:bg-emerald-600 hover:border-emerald-600 hover:text-white transition-colors disabled:opacity-60"
                  disabled={aiBusy}
                >
                  Next
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleCreateWithAi}
                  className="rounded border border-neutral-700 bg-black px-3 py-1 text-xs text-neutral-300 hover:bg-emerald-600 hover:border-emerald-600 hover:text-white transition-colors disabled:opacity-60"
                  disabled={aiBusy}
                >
                  {aiBusy ? 'Creating…' : 'Create with AI'}
                </button>
              )}
            </div>
          </div>
        </div>
        )}
      </AppDialog>

      <AppDialog
        open={existsConfirmOpen}
        onOpenChange={(open) => {
          if (!open) {
            setExistsConfirmBusy(false);
            pendingPublishRef.current = null;
            pendingExistsMaxVersionRef.current = 0;
          }
          setExistsConfirmOpen(open);
        }}
        title="Workflow already exists"
        description={`A workflow with this key already exists (latest version: ${pendingExistsMaxVersionRef.current}). Create a new version?`}
        size="md"
        footer={
          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              className="px-3 py-1 rounded bg-black border border-neutral-700 text-neutral-300 hover:bg-rose-600 hover:border-rose-600 hover:text-white transition-colors disabled:opacity-70"
              onClick={() => setExistsConfirmOpen(false)}
              disabled={existsConfirmBusy}
            >
              Discard
            </button>
            <button
              type="button"
              className="px-3 py-1 rounded bg-black border border-neutral-700 text-neutral-300 hover:bg-emerald-600 hover:border-emerald-600 hover:text-white transition-colors disabled:bg-black disabled:text-neutral-600 disabled:cursor-not-allowed"
              disabled={existsConfirmBusy}
              onClick={async () => {
                const pending = pendingPublishRef.current;
                if (!pending) {
                  setExistsConfirmOpen(false);
                  return;
                }
                setExistsConfirmBusy(true);
                try {
                    v2Mutation.mutate(pending);
                    setExistsConfirmOpen(false);
                } finally {
                  setExistsConfirmBusy(false);
                }
              }}
            >
              Create new version
            </button>
          </div>
        }
      >
        <div className="text-sm text-neutral-300">
          Publishing will create a new immutable catalog entry for the same workflow key.
        </div>
      </AppDialog>
    </>
  );
}

interface LabeledInputProps {
  label: string;
  value: string;
  placeholder?: string;
  description?: string;
  onChange: (value: string) => void;
}

function LabeledInput({ label, value, placeholder, description, onChange }: LabeledInputProps) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-xs uppercase tracking-wide text-neutral-400">{label}</label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="bg-black border border-neutral-700 rounded px-2 py-1 text-sm outline-none focus:ring-1 focus:ring-indigo-500"
      />
      {description && <p className="text-[11px] leading-snug text-neutral-500">{description}</p>}
    </div>
  );
}

interface LabeledTextareaProps {
  label: string;
  value: string;
  placeholder?: string;
  description?: string;
  onChange: (value: string) => void;
  rows?: number;
}

function LabeledTextarea({ label, value, placeholder, description, onChange, rows = 8 }: LabeledTextareaProps) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-xs uppercase tracking-wide text-neutral-400">{label}</label>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={rows}
        className="bg-black border border-neutral-700 rounded px-2 py-2 text-sm font-mono outline-none focus:ring-1 focus:ring-indigo-500"
      />
      {description && <p className="text-[11px] leading-snug text-neutral-500">{description}</p>}
    </div>
  );
}
